import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AvailabilityMatrix, AvailabilityPicker } from '../components/AvailabilityPicker'
import { IllustrationSlot } from '../components/IllustrationSlot'
import { resortById } from '../data/resorts'
import { availabilityWindow } from '../lib/dates'
import { encodePayload, decodePayload } from '../lib/share'
import { useStore } from '../lib/state'
import type { Trip, TripRsvp, RsvpStatus } from '../lib/types'
import { uid } from '../lib/storage'

interface TripSharePayload {
  v: 1
  id: string
  resortId: string
  startDate: string
  endDate: string
  hostName: string
  note?: string
  hostAvailability?: string[]
}

async function fetchRsvps(tripId: string): Promise<{ rsvps: TripRsvp[]; trip?: TripSharePayload | null }> {
  try {
    const res = await fetch(`/api/trips/${encodeURIComponent(tripId)}`)
    if (!res.ok) return { rsvps: [] }
    const data = (await res.json()) as { rsvps?: TripRsvp[]; trip?: TripSharePayload | null }
    return { rsvps: data.rsvps ?? [], trip: data.trip ?? null }
  } catch {
    return { rsvps: [] }
  }
}

async function postRsvp(tripId: string, rsvp: TripRsvp, meta: TripSharePayload): Promise<TripRsvp[]> {
  try {
    const res = await fetch(`/api/trips/${encodeURIComponent(tripId)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ rsvp, trip: meta }),
    })
    if (!res.ok) throw new Error('fail')
    const data = (await res.json()) as { rsvps?: TripRsvp[] }
    return data.rsvps ?? []
  } catch {
    const key = `passfolio.trip.rsvp.${tripId}`
    const existing: TripRsvp[] = JSON.parse(localStorage.getItem(key) || '[]')
    const next = [...existing.filter((r) => r.name.toLowerCase() !== rsvp.name.toLowerCase()), rsvp]
    localStorage.setItem(key, JSON.stringify(next))
    return next
  }
}

export function TripPublic() {
  const { tripId } = useParams()
  const { state, updateTripRsvps } = useStore()
  const [name, setName] = useState('')
  const [avail, setAvail] = useState<string[]>([])
  const [rsvps, setRsvps] = useState<TripRsvp[]>([])
  const [toast, setToast] = useState<string | null>(null)
  const [fromQuery, setFromQuery] = useState<TripSharePayload | null>(null)
  const [fromApi, setFromApi] = useState<TripSharePayload | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const d = params.get('d')
    if (d) setFromQuery(decodePayload<TripSharePayload>(d))
  }, [])

  const localTrip: Trip | undefined = useMemo(
    () => state.trips.find((t) => t.id === tripId),
    [state.trips, tripId],
  )

  const tripMeta: TripSharePayload | null = useMemo(() => {
    if (localTrip) {
      return {
        v: 1,
        id: localTrip.id,
        resortId: localTrip.resortId,
        startDate: localTrip.startDate,
        endDate: localTrip.endDate,
        hostName: localTrip.hostName,
        note: localTrip.note,
        hostAvailability: localTrip.hostAvailability,
      }
    }
    return fromQuery ?? fromApi
  }, [localTrip, fromQuery, fromApi])

  const windowDays = useMemo(
    () => (tripMeta ? availabilityWindow(tripMeta.startDate, tripMeta.endDate, 2) : []),
    [tripMeta],
  )

  useEffect(() => {
    if (!tripId) return
    fetchRsvps(tripId).then(({ rsvps: list, trip }) => {
      if (trip && !fromQuery && !localTrip) setFromApi(trip)
      if (list.length) {
        setRsvps(list)
        if (localTrip) updateTripRsvps(tripId, list)
      } else if (localTrip) {
        setRsvps(localTrip.rsvps)
      } else {
        const key = `passfolio.trip.rsvp.${tripId}`
        setRsvps(JSON.parse(localStorage.getItem(key) || '[]'))
      }
    })
  }, [tripId, localTrip, updateTripRsvps, fromQuery])

  useEffect(() => {
    if (!tripMeta) return
    // default joiner's availability to core trip days when empty
    if (avail.length === 0 && windowDays.length) {
      setAvail(windowDays.filter((d) => d >= tripMeta.startDate && d <= tripMeta.endDate))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripMeta?.id, windowDays.length])

  const resort = tripMeta ? resortById(tripMeta.resortId) : undefined

  const shareWithData = () => {
    if (!tripMeta || !tripId) return
    const url = `${window.location.origin}/trip/${tripId}?d=${encodePayload(tripMeta)}`
    navigator.clipboard?.writeText(url)
    setToast('Share link with trip details copied')
    setTimeout(() => setToast(null), 2200)
  }

  const respond = async (status: RsvpStatus) => {
    if (!tripId || !tripMeta || !name.trim()) {
      setToast('Add your name first')
      setTimeout(() => setToast(null), 1800)
      return
    }
    if (avail.length === 0) {
      setToast('Pick at least one free day')
      setTimeout(() => setToast(null), 1800)
      return
    }
    const rsvp: TripRsvp = {
      id: uid('rsvp'),
      name: name.trim(),
      status,
      at: new Date().toISOString(),
      availableDates: avail,
    }
    const next = await postRsvp(tripId, rsvp, tripMeta)
    setRsvps(next)
    if (localTrip) updateTripRsvps(tripId, next)
    setToast(status === 'join' ? "You're in" : 'Marked as maybe')
    setTimeout(() => setToast(null), 2000)
  }

  if (!tripMeta) {
    return (
      <div className="app-shell">
        <div className="card stack">
          <h2>Trip not found on this device</h2>
          <p className="muted">
            Ask the host to re-copy the link so the mountain and dates travel with the URL. Or open this
            page from the same browser that created the trip.
          </p>
          <Link className="btn primary" to="/trips">
            Back to trips
          </Link>
        </div>
      </div>
    )
  }

  const hostDates = tripMeta.hostAvailability ?? localTrip?.hostAvailability ?? []
  const matrixRows = [
    { name: `${tripMeta.hostName} (host)`, dates: hostDates, highlight: true },
    ...rsvps.map((r) => ({ name: r.name, dates: r.availableDates ?? [] })),
  ]

  return (
    <div className="app-shell">
      <div className="row between" style={{ marginBottom: 16 }}>
        <Link to="/" className="brand">
          <span className="brand-mark" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
              <path d="M3 18 L10 6 l3.5 5.5 L16 9 l5 9 H3z" fill="#2A9D8F" opacity="0.95" />
              <circle cx="18" cy="5" r="2" fill="#5EC4E8" />
            </svg>
          </span>
          <span>Passfolio</span>
        </Link>
        {localTrip && (
          <button className="btn sm" onClick={shareWithData}>
            Copy link with details
          </button>
        )}
      </div>

      <div className="season-card-preview stack">
        <div className="row between" style={{ alignItems: 'flex-start' }}>
          <div className="kicker">Trip card</div>
          <IllustrationSlot kind="trip" caption="art later" />
        </div>
        <h1 style={{ marginBottom: 0 }}>{resort?.name ?? tripMeta.resortId}</h1>
        <div className="muted">
          {tripMeta.startDate} → {tripMeta.endDate}
          {resort ? ` · ${resort.state}, ${resort.country}` : ''}
        </div>
        <div className="row">
          <span className="chip">Host · {tripMeta.hostName}</span>
        </div>
        {tripMeta.note && <p className="muted">{tripMeta.note}</p>}

        <div className="metric-grid">
          <div className="metric">
            <div className="label">Joining</div>
            <div className="value">{rsvps.filter((r) => r.status === 'join').length}</div>
          </div>
          <div className="metric">
            <div className="label">Maybe</div>
            <div className="value">{rsvps.filter((r) => r.status === 'maybe').length}</div>
          </div>
          <div className="metric">
            <div className="label">Host free</div>
            <div className="value">{hostDates.length}</div>
          </div>
          <div className="metric">
            <div className="label">Window</div>
            <div className="value" style={{ fontSize: 16 }}>
              {windowDays.length}d
            </div>
          </div>
        </div>

        {(hostDates.length > 0 || rsvps.some((r) => (r.availableDates ?? []).length > 0)) && (
          <div className="stack">
            <div className="kicker">Crew availability</div>
            <AvailabilityMatrix days={windowDays} rows={matrixRows} />
          </div>
        )}

        <div className="card stack create-panel" style={{ background: 'rgba(255,255,255,0.85)', boxShadow: 'none' }}>
          <h3 style={{ margin: 0 }}>RSVP + availability</h3>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            No account needed — name, free days, then Join.
          </p>
          <div className="field">
            <label>Your name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex" />
          </div>
          <AvailabilityPicker
            days={windowDays}
            selected={avail}
            onChange={setAvail}
            tripStart={tripMeta.startDate}
            tripEnd={tripMeta.endDate}
            label="When can you go?"
          />
          <div className="row">
            <button className="btn primary lg" onClick={() => respond('join')}>
              Join trip
            </button>
            <button className="btn lg" onClick={() => respond('maybe')}>
              Maybe
            </button>
          </div>
        </div>

        <div className="stack">
          <div className="kicker">Crew</div>
          {rsvps.length === 0 ? (
            <div className="muted">No RSVPs yet — be the first.</div>
          ) : (
            <div className="list">
              {rsvps.map((r) => (
                <div className="list-item" key={r.id}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{r.name}</div>
                    <div className="faint" style={{ fontSize: 11, marginTop: 2 }}>
                      {(r.availableDates ?? []).length
                        ? `${(r.availableDates ?? []).length} day${(r.availableDates ?? []).length === 1 ? '' : 's'} free`
                        : 'No days set'}
                    </div>
                  </div>
                  <span className={`chip ${r.status === 'join' ? 'indy' : ''}`}>
                    {r.status === 'join' ? 'Joining' : 'Maybe'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}
